import {SearchTerm} from "@/poster-gallery/classes/search-term";
import {ContentTypeFilter} from "@/poster-gallery/classes/content-type-filter";
import {AdvancedFilterFactory} from "@/poster-gallery/classes/advanced-filters/advanced-filter-factory";

/**
 * Class to handle view and url-building logic for when
 * a user updates their chosen display size (from the sidebar / mobile modal)
 */
export class DisplaySizeFilter {
    static filterType = 'displaySize';
    static allFilterOption = 'all';

    static displaySizeActiveClass = '-display-size-active';
    static displaySizeHasActiveSubsizeClass = '-display-size-with-active-subsize';
    static displaySizeLoadingClass = '-display-size-loading';
    static displaySizeDisabledClass = '-display-size-disabled';
    static displaySizeDisabledActiveClass = '-display-size-disabled-active';
    static displaySizeClasses = `${DisplaySizeFilter.displaySizeLoadingClass} ${DisplaySizeFilter.displaySizeActiveClass} ${DisplaySizeFilter.displaySizeHasActiveSubsizeClass} ${DisplaySizeFilter.displaySizeDisabledClass} ${DisplaySizeFilter.displaySizeDisabledActiveClass}`;

    static LOADING_STATE = 'loading';
    static ACTIVE_STATE = 'active';
    static INACTIVE_STATE = 'inactive';
    static DISABLED_STATE = 'disabled';
    static DISABLED_ACTIVE_STATE = 'disabled-active';
    static ACTIVE_SUBSIZE_STATE = 'active-subsize';

    static getStateClassMapForDisplaySizeOptions() {
        const map = {};
        map[this.LOADING_STATE] = this.displaySizeLoadingClass;
        map[this.ACTIVE_STATE] = this.displaySizeActiveClass;
        map[this.INACTIVE_STATE] = '';
        map[this.DISABLED_STATE] = this.displaySizeDisabledClass;
        map[this.DISABLED_ACTIVE_STATE] = this.displaySizeDisabledActiveClass;
        map[this.ACTIVE_SUBSIZE_STATE] = this.displaySizeHasActiveSubsizeClass;
        return map;
    }

    /**
     * @param {string} displaySize
     */
    constructor(displaySize) {
        this.displaySize = DisplaySizeFilter.getValidDisplaySize(displaySize);
        this.oldDisplaySize = this.displaySize;
        this.setActiveState();
    }

    /**
     * @return {boolean}
     */
    wasFilterUpdated() {
        return this.oldDisplaySize !== this.displaySize;
    }

    resetOldFilterState() {
        return this.oldDisplaySize = this.displaySize;
    }

    setActiveState() {
        this.state = this.isFilterActive() ? DisplaySizeFilter.ACTIVE_STATE : DisplaySizeFilter.INACTIVE_STATE;
    }

    setLoadingState() {
        this.state = DisplaySizeFilter.LOADING_STATE;
    }

    /**
     * @return {boolean}
     */
    isLoadingState() {
        return this.state === DisplaySizeFilter.LOADING_STATE;
    }

    setDisabledState() {
        this.state = DisplaySizeFilter.DISABLED_STATE;
    }

    /**
     * @return {boolean}
     */
    isDisabledState() {
        return this.state === DisplaySizeFilter.DISABLED_STATE;
    }

    /**
     * @param {string} state
     */
    setState(state) {
        this.state = state;
    }

    /**
     * @return {string}
     */
    getState() {
        return this.state;
    }

    /**
     * @param {jQuery} displaySizeOption
     * @return {DisplaySizeFilter}
     */
    static fromFilterOption(displaySizeOption) {
        return new this(displaySizeOption.closest('.js-size-filter').attr('data-size-name'));
    }

    /**
     * @param {jQuery} chosenFilterOption
     * @return {boolean}
     */
    static isChosenDisplaySizeActive(chosenFilterOption) {
        const chosenDisplaySize = chosenFilterOption.closest('.js-size-filter');
        return chosenDisplaySize.hasClass(this.displaySizeActiveClass)
            || chosenDisplaySize.hasClass(this.displaySizeHasActiveSubsizeClass);
    }

    /**
     * @param {string} displaySize
     * @return {string}
     */
    static getValidDisplaySize(displaySize) {
        if ($(`.js-size-filter[data-size-name=${displaySize}]`).length > 0) {
            return displaySize;
        }
        return DisplaySizeFilter.allFilterOption;
    }

    /**
     * @return {boolean}
     */
    isFilterActive() {
        return this.displaySize !== DisplaySizeFilter.allFilterOption;
    }

    /**
     * @return {string}
     */
    getPopStateInfo() {
        return this.displaySize;
    }

    updateView() {
        const chosenDisplaySize = this.getChosenDisplaySize();
        this.markChosenOptionAsSelected(chosenDisplaySize);
        this.updateParentDisplaySizeOption(chosenDisplaySize);
        this.updateCloseSubsizesButton();
        this.updateMainPanelForMobileFilters(chosenDisplaySize);
        this.hideSubsizesCarouselIfNeeded();
    }

    /**
     * If we move between subsizes of the same parent size, the carousel is not removed.
     */
    hideSubsizesCarouselIfNeeded() {
        const chosenDisplaySize = this.getChosenDisplaySizeFromSizesSidebar().filter('.-subsize'),
            oldDisplaySize = this.getDisplaySizeFromSizesSidebar(this.oldDisplaySize).filter('.-subsize'),
            chosenSubsizeParentName = this.getParentDisplaySizeName(chosenDisplaySize),
            oldSubsizeParentName = this.getParentDisplaySizeName(oldDisplaySize);

        if (chosenSubsizeParentName === oldSubsizeParentName) {
            return;
        }
        PMW.posterGallery.removeSubsizesCarousel();
    }

    /**
     * @param {jQuery} displaySubsize
     * @return {string|null}
     */
    getParentDisplaySizeName(displaySubsize) {
        if (displaySubsize.length === 0) {
            return null;
        }
        const subsizesContainer = displaySubsize.closest('.js-subsizes-container');

        if (subsizesContainer.length === 0) {
            return null;
        }
        return subsizesContainer.attr('data-parent-size-name');
    }

    updateCloseSubsizesButton() {
        $('.js-close-subsizes-list').toggleClass('-close-subsizes-list-disabled', this.isLoadingState());
    }

    /**
     * @return {DisplaySizeFilter}
     */
    cloneFilter() {
        const clonedFilter = new DisplaySizeFilter(this.displaySize);
        clonedFilter.setState(this.getState());
        return clonedFilter;
    }

    /**
     * @param {jQuery} chosenDisplaySize
     */
    updateMainPanelForMobileFilters(chosenDisplaySize) {
        const chosenDisplaySubsizeMobileOption = chosenDisplaySize.filter('.js-mobile-size-option.-subsize');

        if (chosenDisplaySubsizeMobileOption.length === 0) {
            this.updateMainDropdownForChosenDisplaySize(null, chosenDisplaySize.filter('.js-mobile-size-option'), true);
            return;
        }
        const displaySizeUriForSubsize = chosenDisplaySubsizeMobileOption.closest('.js-sliding-panel').attr('data-parent-size-name'),
            displaySizeParentOption = $(`.js-size-filter.js-mobile-size-option[data-size-name=${displaySizeUriForSubsize}]`).not('.-subsize'),
            isAllOption = displaySizeUriForSubsize === chosenDisplaySubsizeMobileOption.attr('data-size-name');
        this.updateMainDropdownForChosenDisplaySize(chosenDisplaySubsizeMobileOption, displaySizeParentOption, isAllOption);
    }

    /**
     * @param {jQuery} chosenDisplaySize
     */
    updateParentDisplaySizeOption(chosenDisplaySize) {
        this.updateDisplaySizeTextForUnselectedDisplaySizes();

        const chosenDisplaySizeOptionInSidebar = chosenDisplaySize.filter('.js-display-size-sidebar-option'),
            displaySizeUriForSubsize = chosenDisplaySizeOptionInSidebar.closest('.js-subsizes-container').attr('data-parent-size-name'),
            displaySizeParentOption = $(`.js-size-filter.js-parent-size-option:not(.-subsize)[data-size-name=${displaySizeUriForSubsize}]`),
            isAllOption = displaySizeUriForSubsize === chosenDisplaySize.attr('data-size-name');

        const textElToUpdate = displaySizeParentOption.find('.js-chosen-subsize-display-name'),
            textToShow = isAllOption ? '' : $(chosenDisplaySizeOptionInSidebar[0]).find('.js-size-display-name').text(),
            stateClassMap = DisplaySizeFilter.getStateClassMapForDisplaySizeOptions(),
            stateForSelectedOption = this.getStateForSelectedOption(true);

        textElToUpdate.text(textToShow);
        displaySizeParentOption.removeClass(DisplaySizeFilter.displaySizeClasses).addClass(stateClassMap[stateForSelectedOption]);
    }

    updateDisplaySizeTextForUnselectedDisplaySizes() {
        const allDisplaySizes = $('.js-size-filter.js-parent-size-option'),
            unselectedDisplaySizes = allDisplaySizes.not('.-display-size-active');
        unselectedDisplaySizes.find('.js-chosen-subsize-display-name').text('');
    }

    /**
     * @param {jQuery|null} chosenDisplaySubsizeMobileOption
     * @param {jQuery} displaySizeParentOption
     * @param {boolean} isAllOption
     */
    updateMainDropdownForChosenDisplaySize(chosenDisplaySubsizeMobileOption, displaySizeParentOption, isAllOption) {
        const textElToUpdate = $('.js-show-display-size-options .js-dropdown-placeholder');
        let textToShow = isAllOption ? displaySizeParentOption.attr('data-display-name') : chosenDisplaySubsizeMobileOption.attr('data-display-name');

        if (!this.isFilterActive()) {
            textToShow = textElToUpdate.attr('data-default-text');
        }
        textElToUpdate.text(textToShow);
    }

    /**
     * @return {jQuery}
     */
    getChosenDisplaySize() {
        return $(`.js-size-filter[data-size-name=${this.displaySize}]`);
    }

    /**
     * @return {jQuery}
     */
    getChosenDisplaySizeFromSizesSidebar() {
        return this.getDisplaySizeFromSizesSidebar(this.displaySize);
    }

    /**
     * @param displaySize
     * @return {jQuery}
     */
    getDisplaySizeFromSizesSidebar(displaySize) {
        return $(`.js-sizes-sidebar .js-size-filter[data-size-name=${displaySize}]`);
    }

    /**
     * @param {jQuery} chosenDisplaySize
     */
    markChosenOptionAsSelected(chosenDisplaySize) {
        const allOptionsForDisplaySize = $('.js-size-filter'),
            stateClassMap = DisplaySizeFilter.getStateClassMapForDisplaySizeOptions(),
            stateForUnselectedOptions = (this.isLoadingState() || this.isDisabledState()) ? DisplaySizeFilter.DISABLED_STATE : DisplaySizeFilter.INACTIVE_STATE,
            stateForSelectedOption = this.getStateForSelectedOption();

        allOptionsForDisplaySize.removeClass(DisplaySizeFilter.displaySizeClasses).addClass(stateClassMap[stateForUnselectedOptions]);
        chosenDisplaySize.removeClass(DisplaySizeFilter.displaySizeClasses).addClass(stateClassMap[stateForSelectedOption]);
    }

    getStateForSelectedOption(isParentDisplaySize = false) {
        if (this.isLoadingState()) {
            return DisplaySizeFilter.LOADING_STATE;
        }

        if (this.isDisabledState()) {
            return DisplaySizeFilter.DISABLED_ACTIVE_STATE;
        }

        if (!isParentDisplaySize) {
            return DisplaySizeFilter.ACTIVE_STATE;
        }
        return DisplaySizeFilter.ACTIVE_SUBSIZE_STATE;
    }

    /**
     * @param {DisplaySizeFilter} displaySizeFilter
     */
    handleUpdatedFilter(displaySizeFilter) {
        if (this.displaySize === displaySizeFilter.displaySize) {
            return;
        }
        this.displaySize = displaySizeFilter.displaySize;
        this.updateView();
    }

    /**
     * @return {string}
     */
    updateUrl() {
        const chosenDisplaySize = this.getChosenDisplaySizeFromSizesSidebar(),
            displaySizeUrl = chosenDisplaySize.find('.js-size-href').attr('href'),
            posterGalleryFilters = PMW.posterGallery.getPosterGalleryFiltersForUpdate(),
            contentTypeFilter = posterGalleryFilters.getContentTypeFilter();

        this.updateSearchTermFilterBasedOnDisplaySizeUrl(displaySizeUrl);
        this.handleDefaultContentTypeFilterForDisplaySize(contentTypeFilter, chosenDisplaySize);
        const urlWithUpdatedContentType = contentTypeFilter.updateUrl(displaySizeUrl);

        let updatedUrlObj = new URL(urlWithUpdatedContentType);
        return posterGalleryFilters.updateUrlForAdvancedFilters(updatedUrlObj).toString();
    }

    getUrlForChosenDisplaySize() {
        const chosenDisplaySize = this.getChosenDisplaySizeFromSizesSidebar();
        return chosenDisplaySize.find('.js-size-href').attr('href');
    }

    /**
     * If the display size url is not for a search page, we deactivate the search filter
     *
     * @param {string} displaySizeUrl
     */
    updateSearchTermFilterBasedOnDisplaySizeUrl(displaySizeUrl) {
        if (!PMW.posterGallery.isSearchPage(displaySizeUrl)) {
            const newSearchTermFilter = new SearchTerm('');
            PMW.posterGallery.getPosterGalleryFiltersForUpdate().searchTermFilter.handleUpdatedFilter(newSearchTermFilter);
        }
    }

    /**
     * If the user did has not selected any content type by themselves,
     * we activate the default content type for the display size
     *
     * @param {ContentTypeFilter} contentTypeFilter
     * @param {jQuery} chosenDisplaySize
     */
    handleDefaultContentTypeFilterForDisplaySize(contentTypeFilter, chosenDisplaySize) {
        if (!contentTypeFilter.wasOptionSelectedByUser) {
            contentTypeFilter.revertToLastUserChosenOption();
        }

        if (!contentTypeFilter.isFilterActive()) {
            const defaultContentTypeFilterForDisplaySize = AdvancedFilterFactory.fromArrayKeyAndFilterOption(contentTypeFilter.advancedFiltersArrayKey, chosenDisplaySize.attr('data-default-content-type'));
            defaultContentTypeFilterForDisplaySize.setWasOptionSelectedByUser(false);
            contentTypeFilter.handleUpdatedFilter(defaultContentTypeFilterForDisplaySize);
        }
    }

    /**
     * @param {string|null} html
     * @return {DisplaySizeFilter}
     */
    static fromPageHtml(html = null) {
        let documentJquery = $(document);

        if (html !== null) {
            documentJquery = $(html);
        }
        const displaySize = documentJquery.find(`.js-sizes-sidebar .js-size-filter${'.' + DisplaySizeFilter.displaySizeActiveClass}`).attr('data-size-name');
        return new this(displaySize);
    }
}